import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Dumbbell,
  Megaphone,
  TrendingUp,
  Cpu,
  Users as UsersIcon,
  CreditCard,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  LifeBuoy,
  Calendar,
  Gift,
  ShoppingBag,
  Bell
} from 'lucide-react';
import NotificationCenter from '../pages/dashboard/NotificationCenter';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const menuItems = [
    { name: 'Overview', path: '/admin', icon: LayoutDashboard },
    { name: 'Gyms', path: '/admin/gyms', icon: Dumbbell },
    { name: 'Rewards', path: '/admin/rewards', icon: Gift },
    { name: 'Coin Store', path: '/admin/store', icon: ShoppingBag },
    { name: 'Ads', path: '/admin/ads', icon: Megaphone },
    { name: 'Analytics', path: '/admin/analytics', icon: TrendingUp },
    { name: 'Devices', path: '/admin/devices', icon: Cpu },
    { name: 'Users', path: '/admin/users', icon: UsersIcon },
    { name: 'Billing', path: '/admin/billing', icon: CreditCard },
    { name: 'Settings', path: '/admin/settings', icon: SettingsIcon },
    { name: 'Reports', path: '/admin/reports', icon: LifeBuoy },
    { name: 'Bookings', path: '/admin/bookings', icon: Calendar },
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-[#0B0F19] text-gray-200 overflow-hidden font-sans">
      {/* Sidebar Mobile Backdrop */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-25 bg-black/60 lg:hidden backdrop-blur-sm transition-opacity duration-300"
        />
      )}

      {/* Sidebar for Desktop & Mobile */}
      <aside className={`fixed inset-y-0 left-0 z-30 w-64 bg-[#0F172A] border-r border-[#1E293B] transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-0 transition-transform duration-300 ease-in-out flex flex-col`}>
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-[#1E293B] bg-[#0B0F19]">
          <Link to="/admin" className="flex items-center space-x-2">
            <span className="text-[#6CD800] font-black text-2xl tracking-wider font-bebas">FITSCAN</span>
            <span className="text-xs bg-[#1E293B] text-[#6CD800] px-2 py-0.5 rounded font-semibold border border-[#6CD800]/20">ADMIN</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Sidebar Links */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          {menuItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group ${active
                  ? 'bg-[#1E293B] text-[#6CD800] border-l-4 border-[#6CD800] pl-3'
                  : 'text-gray-400 hover:bg-[#1E293B]/50 hover:text-white'
                  }`}
              >
                <Icon
                  size={18}
                  className={`mr-3 transition-colors ${active ? 'text-[#6CD800]' : 'text-gray-400 group-hover:text-white'}`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer / Logout */}
        <div className="p-4 border-t border-[#1E293B]">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors group"
          >
            <LogOut size={18} className="mr-3 text-red-400 group-hover:text-red-300" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 flex items-center justify-between px-6 bg-[#0F172A] border-b border-[#1E293B] z-10">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-400 hover:text-white focus:outline-none"
          >
            <Menu size={24} />
          </button>

          <div className="flex items-center space-x-4 ml-auto">
            {/* Notification Bell */}
            <button
              className="relative p-2 rounded-full bg-[#1E293B] border border-gray-800 hover:border-[#6CD800]/50 transition-all group cursor-pointer"
              aria-label="Notifications"
              onClick={() => setNotifOpen(true)}
            >
              <Bell size={18} className="text-gray-400 group-hover:text-[#6CD800] transition-colors" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4">
                  <span className="animate-pulse absolute inline-flex h-full w-full rounded-full bg-[#6CD800]/40 opacity-75 blur-[2px]"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-[#6CD800] text-zinc-950 font-black text-[9px] items-center justify-center">
                    {unreadCount}
                  </span>
                </span>
              )}
            </button>

            <div className="flex flex-col text-right">
              <span className="text-sm font-medium text-white">Super Admin</span>
              <span className="text-xs text-gray-400">admin@fitscan.in</span>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#1E293B] border border-[#6CD800]/30 flex items-center justify-center text-[#6CD800] font-bold">
              SA
            </div>
          </div>
        </header>

        {/* Page Content Panel */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-[#0B0F19] p-6 lg:p-8">
          {children || <Outlet />}
        </main>
      </div>

      <NotificationCenter
        isOpen={notifOpen}
        onClose={() => setNotifOpen(false)}
        onNavigate={(menu) => {
          if (menu === "Store Claims") {
            navigate("/admin/store");
          }
          setNotifOpen(false);
        }}
        username="superadmin"
        onUnreadCountChange={setUnreadCount}
      />
    </div>
  );
};

export default AdminLayout;
